'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetSectionPosition } from '@src/components/pl-base-widget/contexts/widget-page-order'
import { usePLPageMetaOptional } from '@src/components/pl-page-context'
import { CategoriesShowcaseV2 } from '@src/SectionRenderer/sections/categoriesShowcaseV2/CategoriesShowcaseV2'

import { buildCategoriesShowcaseConfig } from '../categories-showcase-section-builders'
import { PLSectionTrackingWrapper } from '../pl-section-tracking-wrapper'

import type { CategoriesShowcaseWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const CategoriesShowcaseWidgetComponent = (props: PLTypedWidgetProps<CategoriesShowcaseWidgetData>) => {
  const data = props.data.categoriesShowcaseData
  const tracker = usePLPageMetaOptional()?.sectionTracker
  const sectionPosition = useWidgetSectionPosition(props.id)

  // Memoized: the page-order context re-renders this widget on every layout-pagination append, and
  // a fresh sectionConfig identity each time would make the tracking emitter's effect tear down and
  // re-create its IntersectionObserver — re-firing IS_VISIBLE for an already-visible section.
  const sectionConfig = useMemo(
    () => (data?.thumbnails.length ? buildCategoriesShowcaseConfig(data, tracker, sectionPosition) : null),
    [data, tracker, sectionPosition],
  )
  if (!sectionConfig) return null

  return (
    <PLSectionTrackingWrapper sectionConfig={sectionConfig} tracker={tracker}>
      <CategoriesShowcaseV2 sectionConfig={sectionConfig} />
    </PLSectionTrackingWrapper>
  )
}

export const CategoriesShowcaseWidget: PLTypedWidgetInterface<
  CategoriesShowcaseWidgetData,
  WidgetType.CATEGORIES_SHOWCASE
> = {
  id: WidgetType.CATEGORIES_SHOWCASE,
  errorBoundary: PLBaseErrorBoundary,
  render: CategoriesShowcaseWidgetComponent,
}
